class ServingOption extends HTMLElement {
  constructor() {
    super();
    this.currentOption = null;
    this.isLoading = false;
    this.initialized = false;
    this.preventRecursion = false;
    this.options = null;
  }

  connectedCallback() { 
    if (this.initialized) return;
    this.initialized = true;

    const optionsId = this.getAttribute('data-options-id');
    if (!optionsId) return;

    const optionsScript = document.getElementById(optionsId);
    if (!optionsScript) return;

    try {
      this.options = JSON.parse(optionsScript.textContent.trim());
      if (!this.currentOption) {
        this.currentOption = this.options[0]; // Set initial option only if not set
      }
      this.render();
      this.setupEventListeners();
    } catch (error) {
      console.error('Error parsing serving options:', error);
    }
  }

  render() {
    if (!this.options) return;
    
    this.innerHTML = `
      <div class="serving-options">
        <div class="serving-options__list" role="tablist">
          ${this.options.map((option, index) => `
            <button 
              class="serving-option__button ${this.currentOption === option ? 'is-active' : ''}"
              role="tab"
              aria-selected="${this.currentOption === option}"
              aria-controls="serving-panel-${index}"
              data-index="${index}"
              ${this.currentOption === option ? 'tabindex="0"' : 'tabindex="-1"'}
            >
              <div class="serving-option__content">
                <span class="serving-option__title">${option.title}</span>
              </div>
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    this.querySelectorAll('.serving-option__button').forEach(button => {
      button.addEventListener('click', (e) => this.handleOptionClick(e));
      button.addEventListener('keydown', (e) => this.handleKeydown(e));
    });
  }

  async handleOptionClick(event) {
    const button = event.currentTarget;
    const index = parseInt(button.dataset.index);
    const selectedOption = this.options[index];

    if (this.isLoading || this.currentOption === selectedOption || this.preventRecursion) return;

    this.updateActiveButton(button);
    this.currentOption = selectedOption;

    // Only update loading state in the button
    button.classList.add('is-loading');
    
    await this.loadProductContent(selectedOption);
    
    button.classList.remove('is-loading');
  }

  handleKeydown(event) {
    const buttons = Array.from(this.querySelectorAll('.serving-option__button'));
    const index = buttons.indexOf(event.currentTarget);
    let newIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        newIndex = (index + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        newIndex = (index - 1 + buttons.length) % buttons.length;
        break;
      default:
        return;
    }

    event.preventDefault();
    buttons[newIndex].click();
    buttons[newIndex].focus();
  }

  updateActiveButton(newActiveButton) {
    this.querySelectorAll('.serving-option__button').forEach(button => {
      const isActive = button === newActiveButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive);
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  }

  async loadProductContent(option) {
    if (!option.handle) return;
    this.isLoading = true;
    
    // Find the product container and purchase block
    const purchaseBlock = this.parentElement.closest('.product-card__purchase-block');
    const productContent = purchaseBlock.parentElement;
    if (!productContent || !purchaseBlock) return;
    
    try {
      const response = await fetch(`/products/${option.handle}?section_id=custom-product-select`);
      if (!response.ok) throw new Error('Product fetch failed');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get the new content elements
      const newContent = doc.querySelector('[data-api-content]');
      if (!newContent) return;

      // Save current serving options state and selected option
      const currentServingOptions = this.closest('.product-card__serving-options');
      const servingOptionsHtml = currentServingOptions.outerHTML;

      // Create containers for smooth transition
      const tempContent = document.createElement('div');
      tempContent.style.position = 'absolute';
      tempContent.style.top = '0';
      tempContent.style.left = '0';
      tempContent.style.width = '100%';
      tempContent.style.opacity = '0';
      tempContent.innerHTML = newContent.innerHTML;

      // Update product info (image, title, description)
      const productImage = tempContent.querySelector('.product-quick-add__image, .product-atf__image');
      const productTitle = tempContent.querySelector('.product-card__title, .product-atf__title');
      const productDescription = tempContent.querySelector('.product-card__description, .product-atf__description');
      const productDescriptionFull = tempContent.querySelector('.product-card__description-full, .product-atf__description-full');
      const reviewStars = tempContent.querySelector('.product-card__reviews, .product-atf__reviews');

      if (productImage) {
        const existingImage = productContent.querySelector('.product-quick-add__image, .product-atf__image');
        if (existingImage) {
          existingImage.innerHTML = productImage.innerHTML;
        }
      }
      if (productTitle) {
        const existingTitle = productContent.querySelector('.product-card__title, .product-atf__title');
        if (existingTitle) {
          existingTitle.innerHTML = productTitle.innerHTML;
        }
      }
      if (productDescription) {
        const existingDescription = productContent.querySelector('.product-card__description, .product-atf__description');
        if (existingDescription) {
          existingDescription.innerHTML = productDescription.innerHTML;
        }
      }
      if (productDescriptionFull) {
        const existingDescriptionFull = productContent.querySelector('.product-card__description-full, .product-atf__description-full');
        if (existingDescriptionFull) {
          existingDescriptionFull.innerHTML = productDescriptionFull.innerHTML;
        }
      }
      if (reviewStars) {
        const existingStars = productContent.querySelector('.product-card__reviews, .product-atf__reviews');
        if (existingStars) {
          existingStars.innerHTML = reviewStars.innerHTML;
        }
      }

      // Update purchase block content
      const newPurchaseBlock = tempContent.querySelector('.product-card__purchase-block');
      if (newPurchaseBlock) {
        // Remove serving options from new content to prevent duplication
        const newServingOptions = newPurchaseBlock.querySelector('.product-card__serving-options');
        if (newServingOptions) {
          newServingOptions.remove();
        }

        // Get the new variant ID and price
        const newForm = newPurchaseBlock.querySelector('product-form form');
        const newVariantId = newForm?.querySelector('input[name="id"]')?.value;
        const newPrice = newForm?.querySelector('[data-js-product-price-extended]')?.innerHTML;

        // Insert serving options at the start of purchase block
        purchaseBlock.innerHTML = servingOptionsHtml + newPurchaseBlock.innerHTML;

        // Update the form with new variant ID
        const form = purchaseBlock.querySelector('product-form form');
        if (form && newVariantId) {
          const variantInput = form.querySelector('input[name="id"]');
          if (variantInput) {
            variantInput.value = newVariantId;
          }
        }

        // Update the price display
        if (newPrice) {
          const priceElement = purchaseBlock.querySelector('[data-js-product-price-extended]');
          if (priceElement) {
            priceElement.innerHTML = newPrice;
          }
        }

        // Reinitialize the serving options without triggering click
        const servingOption = purchaseBlock.querySelector('serving-option');
        if (servingOption) {
          this.preventRecursion = true;
          
          // Preserve current options and selection
          const currentOptions = this.options;
          const currentSelectedOption = this.currentOption;
          
          // Reset and reinitialize
          servingOption.initialized = false;
          servingOption.options = currentOptions;
          servingOption.currentOption = currentSelectedOption;
          servingOption.connectedCallback();
          
          // Update active state
          const buttons = servingOption.querySelectorAll('.serving-option__button');
          const activeButton = buttons[this.options.indexOf(this.currentOption)];
          if (activeButton) {
            servingOption.updateActiveButton(activeButton);
          }
          
          this.preventRecursion = false;
        }

        // Initialize subscription tabs
        const subscriptionTabs = purchaseBlock.querySelector('subscription-tabs');
        if (subscriptionTabs) {
          // Remove old event listeners
          const oldTabs = subscriptionTabs.querySelectorAll('[data-tab]');
          oldTabs.forEach(tab => {
            tab.replaceWith(tab.cloneNode(true));
          });
          
          // Reinitialize the tabs
          if (!subscriptionTabs.initialized) {
            subscriptionTabs.connectedCallback();
          }
        }

        // Reinitialize add to cart functionality
        const product_options = purchaseBlock.querySelectorAll('.delivery-option');
        const radio_inputs = purchaseBlock.querySelectorAll('.delivery-option input[type="radio"]');
        const atcButtons = purchaseBlock.querySelectorAll('.NEW-CUSTOM-ATC');

        // Reset event listeners for delivery options
        product_options.forEach(option => {
          option.addEventListener('click', function(e) {
            option.querySelector('.option-radio').checked = true;
            option.querySelector('.option-radio').dispatchEvent(new Event('change', {bubbles: true}));
            updateFormFromSelectedOption();
          });
        });

        // Reset event listeners for radio inputs
        radio_inputs.forEach(radio => {
          radio.addEventListener('change', function(e) {
            updateFormFromSelectedOption(this);
          });
        });

        // Reset event listeners for ATC buttons
        atcButtons.forEach(button => {
          button.addEventListener('click', function(e) {
            e.preventDefault();
            const selectedOption = purchaseBlock.querySelector('.subscription-content:not(.hidden) .delivery-option input:checked');
            const selectedOptionData = selectedOption?.parentNode;
            // Collect ALL form properties automatically
            const properties = {};
            // Get the form
            const form = button.closest('form');
            if (!form) return;

            // Method 1: Use FormData for standard form inputs
            const formData = new FormData(form);
            for (let [key, value] of formData.entries()) {
              if (key.startsWith('properties[') && value) {
                const propertyName = key.match(/properties\[(.*?)\]/)?.[1];
                if (propertyName) {
                  properties[propertyName] = value;
                }
              }
            }
            if (!selectedOption) return;

            const product = {
              id: selectedOptionData.getAttribute('data-product-id'),
              variant: selectedOptionData.getAttribute('data-product-id'),
              sellingPlanId: selectedOptionData.getAttribute('data-selling-plan') || '',
              properties: {}
            };

            let quantity = 1;
            const quantityInput = document.querySelector('#quantity') ||
              selectedOption.querySelector('#custom_quantity_count');
            if (quantityInput) {
              quantity = parseInt(quantityInput.value, 10) || 1;
            }

            addToCart(product, quantity);
          });
        });

        // Initialize first option
        const firstOption = purchaseBlock.querySelector('.subscription-content:not(.hidden) .delivery-option input[type="radio"]');
        if (firstOption) {
          firstOption.checked = true;
          firstOption.dispatchEvent(new Event('change', {bubbles: true}));
        }

        // Dispatch event to notify other components of the variant change
        document.dispatchEvent(new CustomEvent('variant:changed', {
          detail: { variantId: newVariantId }
        }));

        // Trigger form update
        updateFormFromSelectedOption();
      }

    } catch (error) {
      console.error('Error loading product content:', error);
      productContent.innerHTML = `
        <div class="serving-panel__error">
          <p>There was an error loading the product. Please try again.</p>
        </div>
      `;
    } finally {
      this.isLoading = false;
      this.preventRecursion = false;
    }
  }
}

if (!customElements.get('serving-option')) {
  customElements.define('serving-option', ServingOption);
} 